<template>
  <div class="family-members-wrap">

    <!-- ══════════════════════════════════════
         ADD / EDIT DIALOG
    ══════════════════════════════════════ -->
    <v-dialog v-model="dialog" max-width="560" persistent scrollable>
      <v-card class="fm-dialog-card" :color="isDark ? '#162035' : '#fff'">

        <!-- Header -->
        <div class="fm-dialog-header">
          <div class="fm-dialog-title">
            <v-icon size="20" :color="primaryColor" class="mr-2">
              mdi-account-multiple-plus-outline
            </v-icon>
            {{ editMode ? 'Edit Family Member' : 'Add Family Member' }}
          </div>
          <button class="fm-dialog-close" @click="closeDialog">
            <v-icon size="20" color="#64748B">mdi-close</v-icon>
          </button>
        </div>

        <!-- Body -->
        <v-card-text class="fm-dialog-body pa-0">
          <div class="fm-form-inner">

            <!-- Profile Picture -->
            <div class="fm-field fm-avatar-upload-wrap">
              <div class="fm-avatar-upload" @click="$refs.pictureInput.click()">
                <img
                  v-if="form.picturePreview"
                  :src="form.picturePreview"
                  class="fm-avatar-upload-img"
                  @error="form.picturePreview = null"
                />
                <div
                  v-else
                  class="fm-avatar-upload-placeholder"
                  :style="{ background: primaryColor + '18', color: primaryColor }"
                >
                  <v-icon size="28" :color="primaryColor">mdi-account-outline</v-icon>
                </div>
                <div class="fm-avatar-upload-badge" :style="{ background: primaryColor }">
                  <v-icon size="13" color="white">mdi-camera</v-icon>
                </div>
              </div>
              <input
                ref="pictureInput"
                type="file"
                accept="image/*"
                style="display:none"
                @change="onPictureSelect"
              />
              <span class="fm-avatar-upload-hint">Click to upload photo</span>
            </div>

            <!-- Full Name -->
            <div class="fm-field">
              <label class="fm-label">Full Name</label>
              <input
                v-model="form.name"
                class="fm-input"
                placeholder="Enter full name"
                :class="{ 'fm-input-error': errors.name }"
              />
              <span v-if="errors.name" class="fm-error">{{ errors.name }}</span>
            </div>

            <!-- Relation — now uses PremiumSelect (same as Block / Member) -->
            <div class="fm-field">
              <PremiumSelect
                v-model="form.relation_id"
                label="Relation"
                :required="true"
                :items="relations"
                item-text="name"
                item-value="id"
                placeholder="Select relation"
                icon="mdi-account-heart-outline"
                :error="errors.relation_id"
              />
            </div>

            <!-- Mobile -->
            <div class="fm-field">
              <label class="fm-label">Mobile Number</label>
              <div class="fm-phone-row">
                <div class="fm-country-code-wrap">
                  <vue-country-code
                    @onSelect="onCountrySelect"
                    class="fm-country-code-select"
                    :defaultCountry="form.countryIso || 'IN'"
                    :key="form.countryIso || 'IN'"
                  />
                </div>
                <input
                  v-model="form.mobile_no"
                  class="fm-input"
                  placeholder="Mobile number"
                  style="flex:1"
                  :class="{ 'fm-input-error': errors.mobile_no }"
                />
              </div>
              <span v-if="errors.mobile_no" class="fm-error">{{ errors.mobile_no }}</span>
            </div>

            <!-- Email -->
            <div class="fm-field">
              <label class="fm-label">Email</label>
              <input
                v-model="form.email"
                class="fm-input"
                placeholder="Email address (optional)"
              />
            </div>

            <!-- Profession -->
            <div class="fm-field">
              <label class="fm-label">Profession</label>
              <input
                v-model="form.profession"
                class="fm-input"
                placeholder="Profession (optional)"
              />
            </div>

            <!-- Allow Login Toggle -->
            <div class="fm-toggle-row">
              <div>
                <div class="fm-toggle-title">Allow Login Access</div>
                <div class="fm-toggle-sub">Member can log in using their mobile number</div>
              </div>
              <v-switch
                v-model="form.allow_to_login"
                inset
                hide-details
                dense
                :color="primaryColor"
                class="mt-0 pt-0 flex-shrink-0"
              />
            </div>

            <!-- Unit Access — only shown when login is enabled AND member has more than one unit -->
            <div v-if="form.allow_to_login && memberUnits.length > 1" class="fm-field">
              <label class="fm-label">
                Unit Access <span class="req">*</span>
                <span class="fm-label-note">Select units from this society</span>
              </label>

              <div v-if="loadingUnits" class="fm-units-status">
                <v-progress-circular
                  indeterminate
                  :color="primaryColor"
                  size="18"
                  width="2"
                  class="mr-2"
                />
                Loading units…
              </div>

              <div v-else class="fm-units-list">
                <div
                  v-for="unit in memberUnits"
                  :key="unit.id"
                  class="fm-unit-row"
                  :class="{ 'fm-unit-row--selected': isUnitSelected(unit.id) }"
                  :style="isUnitSelected(unit.id)
                    ? { borderColor: primaryColor, background: primaryColor + '10' }
                    : {}"
                  @click="toggleUnit(unit.id)"
                >
                  <div
                    class="fm-unit-icon-wrap"
                    :style="{ background: primaryColor + '18', color: primaryColor }"
                  >
                    <v-icon size="15" :color="primaryColor">
                      {{ unit.unit_type == 2 ? 'mdi-store-outline' : 'mdi-home-outline' }}
                    </v-icon>
                  </div>
                  <div class="fm-unit-text">
                    <span class="fm-unit-name">
                      {{ unit.flat_name ? unit.flat_name + ' · ' : '' }}{{ unit.house_number }}
                    </span>
                  </div>
                  <!-- Checkbox -->
                  <div
                    class="fm-unit-checkbox"
                    :style="isUnitSelected(unit.id)
                      ? { background: primaryColor, borderColor: primaryColor }
                      : {}"
                  >
                    <v-icon v-if="isUnitSelected(unit.id)" size="13" color="white">
                      mdi-check
                    </v-icon>
                  </div>
                </div>
              </div>
              <span v-if="errors.accessible_unit_ids" class="fm-error">
                {{ errors.accessible_unit_ids }}
              </span>
            </div>

          </div>
        </v-card-text>

        <!-- Footer -->
        <div class="fm-dialog-footer">
          <button class="fm-btn-cancel" @click="closeDialog">Cancel</button>
          <button
            class="fm-btn-save"
            :style="{ background: primaryColor }"
            :disabled="saving"
            @click="saveFamilyMember"
          >
            <v-progress-circular
              v-if="saving"
              indeterminate
              color="white"
              size="16"
              width="2"
              class="mr-2"
            />
            {{ editMode ? 'Update' : 'Add Member' }}
          </button>
        </div>

      </v-card>
    </v-dialog>

    <!-- ══════════════════════════════════════
         CONFIRM DELETE
    ══════════════════════════════════════ -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Remove Family Member"
      message="Are you sure you want to remove this family member?"
      confirm-text="Yes, Remove"
      :loading="deleting"
      @confirm="deleteFamilyMember"
    />

    <!-- ══════════════════════════════════════
         PHOTO VIEWER
    ══════════════════════════════════════ -->
    <MediaViewer
      v-model="mediaViewer.open"
      :items="mediaViewer.items"
      :index="0"
    />

    <!-- ══════════════════════════════════════
         PREMIUM TABLE
    ══════════════════════════════════════ -->
    <PremiumTable
      title="Family Members"
      subtitle="Manage family members of this household"
      add-label="Add Family Member"
      :show-edit="true"
      :show-delete="true"
      @add="openAdd"
      @edit="openEdit($event)"
      @delete="confirmDelete($event.id)"
      :columns="columns"
      :items="familyMembers"
      :loading="loading"
      :total-items="total"
      :page.sync="page"
      :items-per-page.sync="perPage"
      :searchable="false"
    >
      <!-- Name + Avatar cell -->
      <template #col-name="{ item }">
        <div class="fm-profile-cell">
          <div class="fm-avatar-wrap">
            <img
              v-if="item.picture && !item._imgError"
              :src="resolveAvatarUrl(item.picture)"
              class="fm-avatar-img fm-avatar-clickable"
              @error="$set(item, '_imgError', true)"
              @click.stop="openPhoto(resolveAvatarUrl(item.picture), item.name)"
            />
            <div
              v-else
              class="fm-avatar-placeholder"
              :style="{ background: primaryColor + '20', color: primaryColor }"
            >
              {{ item.name ? item.name.charAt(0).toUpperCase() : 'F' }}
            </div>
          </div>
          <span class="fm-name">{{ item.name || '—' }}</span>
        </div>
      </template>

      <!-- Relation cell -->
      <template #col-relation_name="{ item }">
        <span class="fm-relation-pill" v-if="item.relation_name">
          {{ item.relation_name }}
        </span>
        <span v-else class="dash">—</span>
      </template>

      <!-- Mobile cell -->
      <template #col-mobile_no="{ item }">
        <div class="fm-mobile-cell" v-if="item.mobile_no">
          <v-icon size="14" color="#94A3B8" class="mr-1">mdi-phone-outline</v-icon>
          <span>{{ item.country_code ? item.country_code + ' ' : '' }}{{ item.mobile_no }}</span>
        </div>
        <span v-else class="dash">—</span>
      </template>

      <!-- Login Access cell -->
      <template #col-allow_to_login="{ item }">
        <span
          class="fm-login-badge"
          :class="item.allow_to_login ? 'fm-login-on' : 'fm-login-off'"
        >
          <v-icon
            size="10"
            class="mr-1"
            :color="item.allow_to_login ? '#059669' : '#94a3b8'"
          >
            {{ item.allow_to_login ? 'mdi-check-circle-outline' : 'mdi-cancel' }}
          </v-icon>
          {{ item.allow_to_login ? 'Login Enabled' : 'No Login' }}
        </span>
      </template>
    </PremiumTable>

  </div>
</template>

<script>
import ConfirmModal from "../../common/ConfirmModal.vue";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import MediaViewer from "../../common/MediaViewer.vue";
import { mapState, mapMutations } from "vuex";

export default {
  name: "MemberFamilyMembers",
  components: { ConfirmModal, PremiumTable, PremiumSelect, MediaViewer },

  props: {
    member_id:       { type: [String, Number], required: true },
    unit_mapping_id: { type: [String, Number], required: true },
  },

  data() {
    return {
      // List state
      loading:       true,
      familyMembers: [],
      total:         0,
      lastPage:      1,
      page:          1,
      perPage:       10,

      // Media viewer
      mediaViewer: { open: false, items: [] },

      // Form state
      dialog:    false,
      editMode:  false,
      saving:    false,
      form: {
        id:                   null,
        name:                 "",
        relation_id:          "",
        mobile_no:            "",
        country_code:         "+91",
        countryIso:           "IN",
        email:                "",
        profession:           "",
        allow_to_login:       false,
        accessible_unit_ids:  [],
      },
      errors: {},

      // Unit selection for login access
      memberUnits:  [],
      loadingUnits: false,

      // Relations dropdown
      relations: [],

      // Delete state
      deleteDialog: false,
      deleting:     false,
      deleteId:     null,
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor", "isDark"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    columns() {
      return [
        { key: "name",            label: "Member",       width: "220px" },
        { key: "relation_name",   label: "Relation",     width: "140px" },
        { key: "mobile_no",       label: "Mobile",       width: "190px" },
        { key: "allow_to_login",  label: "Login Access", width: "150px" },
      ];
    },
  },

  watch: {
    unit_mapping_id: {
      immediate: true,
      handler(val) {
        if (val && val !== 'null') {
          this.page = 1;
          this.fetchFamilyMembers();
          this.fetchMemberUnits();
        }
      },
    },
    page(newVal, oldVal) {
      if (newVal !== oldVal) this.fetchFamilyMembers();
    },
    perPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.page = 1;
        this.fetchFamilyMembers();
      }
    },
  },

  mounted() {
    this.fetchRelations();
  },

  activated() {
      this.fetchFamilyMembers();
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    // ─────────────────────────────────────────
    // COUNTRY CODE SELECT
    // ─────────────────────────────────────────
    onCountrySelect(event) {
      this.form.country_code = "+" + event.dialCode;
      this.form.countryIso   = event.iso2;
    },

    // ─────────────────────────────────────────
    // PICTURE SELECT
    // ─────────────────────────────────────────
    onPictureSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.form.picture = file;
      this.form.picturePreview = URL.createObjectURL(file);
      // reset input so same file can be re-selected
      event.target.value = "";
    },

    // ─────────────────────────────────────────
    // LIST
    // ─────────────────────────────────────────
    fetchFamilyMembers() {
      this.loading = true;
      this.$axios
        .get(`societyadmin/members/${this.unit_mapping_id}/familyMembers`, {
          params: { page: this.page, perPage: this.perPage },
        })
        .then((res) => {
          if (res.data.status) {
            const d = res.data;
            this.familyMembers = (Array.isArray(d.data) ? d.data : []).map((item) => ({
              ...item,
              _imgError: false,
            }));
            this.total    = d.total    || 0;
            this.lastPage = d.lastPage || Math.ceil((d.total || 0) / this.perPage) || 1;
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    // ─────────────────────────────────────────
    // UNITS
    // ─────────────────────────────────────────
    fetchMemberUnits() {
      this.loadingUnits = true;
      this.$axios
        .get(`societyadmin/members/${this.member_id}/units`)
        .then((res) => {
          if (res.data.status) {
            const societies = Array.isArray(res.data.data) ? res.data.data : [];
            const allUnits = societies.flatMap((s) =>
              (s.units || []).map((u) => ({
                id:           u.unit_mapping_id,
                house_number: u.house_number,
                flat_name:    u.block_name || "",
                unit_type:    u.unit_type,
              }))
            );
            this.memberUnits = allUnits;
          }
        })
        .catch(() => {})
        .finally(() => { this.loadingUnits = false; });
    },

    // ─────────────────────────────────────────
    // RELATIONS
    // ─────────────────────────────────────────
    fetchRelations() {
      this.$axios.get("relations").then((res) => {
        if (Array.isArray(res.data)) {
          this.relations = res.data;
        } else if (res.data && Array.isArray(res.data.data)) {
          this.relations = res.data.data;
        } else {
          this.relations = [];
        }
      }).catch(() => {});
    },

    // ─────────────────────────────────────────
    // UNIT SELECTION HELPERS
    // ─────────────────────────────────────────
    isUnitSelected(unitId) {
      return this.form.accessible_unit_ids.includes(String(unitId));
    },
    toggleUnit(unitId) {
      const id  = String(unitId);
      const idx = this.form.accessible_unit_ids.indexOf(id);
      if (idx === -1) {
        this.form.accessible_unit_ids.push(id);
      } else {
        this.form.accessible_unit_ids.splice(idx, 1);
      }
    },

    // ─────────────────────────────────────────
    // OPEN ADD
    // ─────────────────────────────────────────
    openAdd() {
      this.editMode = false;
      this.errors   = {};
      this.form = {
        id:                   null,
        name:                 "",
        relation_id:          "",
        mobile_no:            "",
        country_code:         "+91",
        countryIso:           "IN",
        email:                "",
        profession:           "",
        allow_to_login:       false,
        accessible_unit_ids:  [],
        picture:              null,
        picturePreview:       null,
      };
      this.dialog = true;
    },

    // ─────────────────────────────────────────
    // OPEN EDIT
    // ─────────────────────────────────────────
    openEdit(item) {
      this.editMode = true;
      this.errors   = {};
      this.$axios
        .get(`societyadmin/familyMembers/${item.id}`)
        .then((res) => {
          const d = (res.data.status && res.data.data) ? res.data.data : item;
          this.form = {
            id:                  d.id || item.id,
            name:                d.name         || "",
            relation_id:         d.relation_id  || (d.relation && d.relation.id) || "",
            mobile_no:           d.mobile_no    || "",
            country_code:        d.country_code || "+91",
            countryIso:          d.countryIso   || "IN",
            email:               d.email        || "",
            profession:          d.profession   || "",
            allow_to_login:      !!d.allow_to_login,
            accessible_unit_ids: (d.accessible_unit_ids || []).map(String),
            picture:             null,
            picturePreview:      d.picture ? this.resolveAvatarUrl(d.picture) : null,
          };
          this.dialog = true;
        })
        .catch(() => {
          this.form = {
            id:                  item.id,
            name:                item.name         || "",
            relation_id:         item.relation_id  || "",
            mobile_no:           item.mobile_no    || "",
            country_code:        item.country_code || "+91",
            countryIso:          item.countryIso   || "IN",
            email:               item.email        || "",
            profession:          item.profession   || "",
            allow_to_login:      !!item.allow_to_login,
            accessible_unit_ids: [],
            picture:             null,
            picturePreview:      item.picture ? this.resolveAvatarUrl(item.picture) : null,
          };
          this.dialog = true;
        });
    },

    closeDialog() {
      this.dialog = false;
    },

    // ─────────────────────────────────────────
    // VALIDATION
    // ─────────────────────────────────────────
    validate() {
      this.errors = {};
      if (!this.form.name || !this.form.name.trim()) {
        this.errors.name = "Full name is required.";
      }
      if (!this.form.relation_id) {
        this.errors.relation_id = "Please select a relation.";
      }
      if (this.form.allow_to_login) {
        if (!this.form.mobile_no || !this.form.mobile_no.trim()) {
          this.errors.mobile_no = "Mobile number is required when login is enabled.";
        }
        // Only validate unit selection when there are multiple units to choose from
        if (this.memberUnits.length > 1 && this.form.accessible_unit_ids.length === 0) {
          this.errors.accessible_unit_ids = "Select at least one unit for login access.";
        }
      }
      return Object.keys(this.errors).length === 0;
    },

    // ─────────────────────────────────────────
    // SAVE
    // ─────────────────────────────────────────
    saveFamilyMember() {
      if (!this.validate()) return;
      this.saving = true;

      const payload = new FormData();
      payload.append("parent_id",      this.member_id);
      payload.append("name",           this.form.name.trim());
      payload.append("relation_id",    this.form.relation_id);
      payload.append("mobile_no",      this.form.mobile_no    || "");
      payload.append("country_code",   this.form.country_code || "+91");
      payload.append("countryIso",     this.form.countryIso   || "IN");
      payload.append("email",          this.form.email        || "");
      payload.append("profession",     this.form.profession   || "");
      payload.append("allow_to_login", this.form.allow_to_login ? "true" : "false");
      payload.append("as_member_access", this.form.allow_to_login ? "true" : "false");

      if (this.form.picture) {
        payload.append("picture", this.form.picture);
      }

      if (this.form.allow_to_login) {
        // If only one unit exists, auto-select it; otherwise use the user's selection
        const unitIds = this.memberUnits.length === 1
          ? [String(this.memberUnits[0].id)]
          : this.form.accessible_unit_ids;

        unitIds.forEach((id) => {
          payload.append("accessible_unit_ids[]", id);
        });
      }

      const request = this.editMode
        ? this.$axios.put(`societyadmin/familyMembers/${this.form.id}`, payload)
        : this.$axios.post(`societyadmin/familyMembers`, payload);

      request
        .then((res) => {
          if (res.data.status) {
            this.dialog = false;
            this.showSuccess(
              res.data.message ||
              (this.editMode ? "Updated successfully." : "Family member added.")
            );
            this.page = 1;
            this.fetchFamilyMembers();
          } else {
            this.showError(res.data.message || "Something went wrong.");
          }
        })
        .catch(() => { this.showError("Something went wrong."); })
        .finally(() => { this.saving = false; });
    },

    // ─────────────────────────────────────────
    // DELETE
    // ─────────────────────────────────────────
    confirmDelete(id) {
      this.deleteId     = id;
      this.deleteDialog = true;
    },

    deleteFamilyMember() {
      this.deleting = true;
      this.$axios
        .delete(`societyadmin/familyMembers/${this.deleteId}`)
        .then((res) => {
          if (res.data.status) {
            this.deleteDialog = false;
            this.showSuccess(res.data.message || "Family member removed.");
            if (this.familyMembers.length === 1 && this.page > 1) this.page--;
            this.fetchFamilyMembers();
          } else {
            this.showError(res.data.message || "Something went wrong.");
          }
        })
        .catch(() => { this.showError("Something went wrong."); })
        .finally(() => { this.deleting = false; });
    },

    showSuccess(msg) {
      this.setAlert({ showAlert: true, variant: "green accent-4", timeout: 2000, message: msg });
    },
    showError(msg) {
      this.setAlert({ showAlert: true, variant: "red accent-3", timeout: 2000, message: msg });
    },

    openPhoto(url, name) {
      this.mediaViewer.items = [{ url, type: "image", name: name || "Photo" }];
      this.mediaViewer.open = true;
    },

    // ─────────────────────────────────────────
    // AVATAR URL RESOLVER
    // ─────────────────────────────────────────
    resolveAvatarUrl(picture) {
      if (!picture) return "";
      if (picture.startsWith("http")) return picture;
      if (picture.startsWith("uploads/") || picture.startsWith("/uploads/")) {
        return `${this.API_BASE_URL}/${picture.replace(/^\//, "")}`;
      }
      if (picture.startsWith("family_members/") || picture.startsWith("members/")) {
        return `${this.API_BASE_URL}/uploads/${picture}`;
      }
      return `${this.API_BASE_URL}/uploads/family_members/pictures/${picture}`;
    },
  },
};
</script>

<style scoped>
.family-members-wrap {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Table Cells ── */
.fm-profile-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.fm-avatar-wrap {
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  border: 1px solid var(--border, #e2e8f0);
  background: var(--surface2);
}
.fm-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.fm-avatar-clickable {
  cursor: pointer;
  transition: opacity 0.15s;
}
.fm-avatar-clickable:hover {
  opacity: 0.85;
}
.fm-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 800;
}
.fm-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx);
}

.fm-relation-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--surface2, #f1f5f9);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 11px;
  font-weight: 700;
  color: var(--tx2, #475569);
}

.fm-mobile-cell {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2);
}

.fm-login-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}
.fm-login-on  { background: rgba(5,150,105,0.09); color: #059669; }
.fm-login-off { background: var(--surface2); color: #94a3b8; border: 1px solid var(--border); }

/* ── Dialog ── */
.fm-dialog-card  { border-radius: 20px !important; }
.fm-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}
.fm-dialog-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 700;
  color: var(--tx);
}
.fm-dialog-close {
  background: none;
  border: none;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.fm-dialog-body  { padding: 0 !important; }
.fm-form-inner   { padding: 18px 20px; display: flex; flex-direction: column; gap: 14px; }
.fm-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--border);
  flex-shrink: 0;
}

/* ── Form elements ── */
.fm-field { display: flex; flex-direction: column; gap: 5px; }
.fm-label {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--tx3);
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.fm-label-note {
  font-weight: 400;
  font-size: 11.5px;
  color: #94a3b8;
}
.req { color: #EF4444; }

.fm-input {
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface2);
  color: var(--tx);
  font-size: 13.5px;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}
.fm-input:focus       { border-color: var(--brand, #0d9972); }
.fm-input-error       { border-color: #EF4444 !important; }
.fm-select            { cursor: pointer; appearance: auto; }
.fm-error             { font-size: 11.5px; color: #EF4444; font-weight: 500; }

/* ── Phone Row ── */
.fm-phone-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.fm-country-code-wrap {
  flex-shrink: 0;
  width: 90px;
}
.fm-country-code-select {
  width: 100%;
  height: 40px;
  border: 1px solid var(--border, rgba(0, 0, 0, 0.1)) !important;
  background-color: var(--surface2) !important;
  border-radius: 10px !important;
  transition: all 0.2s ease;
}
.fm-country-code-select:hover {
  border-color: var(--brand, #0d9972) !important;
}
.theme--dark .fm-country-code-select {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* ── Toggle row ── */
.fm-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: var(--surface2);
  border: 1px solid var(--border);
  border-radius: 12px;
  gap: 12px;
}
.fm-toggle-title { font-size: 13px; font-weight: 600; color: var(--tx); }
.fm-toggle-sub   { font-size: 11.5px; color: var(--tx3); margin-top: 1px; }

/* ── Unit list ── */
.fm-units-status {
  display: flex;
  align-items: center;
  padding: 10px 0;
  font-size: 13px;
  color: var(--tx3);
}
.fm-units-list { display: flex; flex-direction: column; gap: 8px; }

.fm-unit-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1.5px solid var(--border);
  border-radius: 11px;
  cursor: pointer;
  background: var(--surface2);
  transition: all 0.18s;
  user-select: none;
}
.fm-unit-row:hover              { border-color: #94a3b8; }
.fm-unit-row--selected          { background: var(--surface); }
.fm-unit-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.fm-unit-text  { flex: 1; min-width: 0; }
.fm-unit-name  { font-size: 13px; font-weight: 700; color: var(--tx); }

.fm-unit-checkbox {
  width: 21px;
  height: 21px;
  border-radius: 50%;
  border: 1.5px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.18s;
}

/* ── Avatar Upload ── */
.fm-avatar-upload-wrap {
  align-items: center;
  gap: 10px;
}
.fm-avatar-upload {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  cursor: pointer;
  margin: 0 auto;
  flex-shrink: 0;
}
.fm-avatar-upload-img {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border, #E2E8F0);
}
.fm-avatar-upload-placeholder {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed var(--border, #E2E8F0);
  transition: border-color 0.2s;
}
.fm-avatar-upload:hover .fm-avatar-upload-placeholder {
  border-color: var(--brand, #0d9972);
}
.fm-avatar-upload-badge {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.15);
}
.fm-avatar-upload-hint {
  display: block;
  text-align: center;
  font-size: 11px;
  color: var(--tx3, #94a3b8);
  margin-top: 6px;
}

/* ── Dialog buttons ── */
.fm-btn-cancel {
  padding: 0 16px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface2);
  color: var(--tx);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.fm-btn-cancel:hover { background: var(--surface); }
.fm-btn-save {
  display: inline-flex;
  align-items: center;
  padding: 0 18px;
  height: 38px;
  border-radius: 10px;
  border: none;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}
.fm-btn-save:hover    { opacity: 0.88; }
.fm-btn-save:disabled { opacity: 0.55; cursor: not-allowed; }
</style>